import Layout from "@/layout";
import { getRolesForRoute, getRolesWithoutRoute } from "@/utils/roles";
import { isAreaSanitariaDeVigo } from "@/utils/user-types";
import areaSanitariaDeVigoRoutes from "@/modules/areasanitariavigo/routes";
import auditRoutes from "@/modules/audit/routes";
import Vue from "vue";
import Router from "vue-router";
import store from "@/store";

Vue.use(Router);

/**
 * Note: sub-menu only appear when route children.length >= 1
 * Detail see: https://panjiachen.github.io/vue-element-admin-site/guide/essentials/router-and-nav.html
 *
 * hidden: true                   if set true, item will not show in the sidebar(default is false)
 * alwaysShow: true               if set true, will always show the root menu
 *                                if not set alwaysShow, when item has more than one children route,
 *                                it will becomes nested mode, otherwise not show the root menu
 * redirect: noRedirect           if set noRedirect will no redirect in the breadcrumb
 * hiddenOnRol: true              if set true, item will not show in ROL EDITOR
 * name:'router-name'             the name is used by <keep-alive> (must set!!!)
 * meta : {
 roles: ['admin','editor']    control the page roles (you can set multiple roles)
 title: 'title'               the name show in sidebar and breadcrumb (recommend set)
 icon: 'svg-name'             the icon show in the sidebar
 noCache: true                if set true, the page will no be cached(default is false)
 breadcrumb: false            if set false, the item will hidden in breadcrumb(default is true)
 hideFromTree: true             if set true, item will not show in the roles drawer
 activeMenu: '/example/list'  if set path, the sidebar will highlight the path you set
 }
 */
// import Fleet from "@/views/fleet/index";

/**
 * constantRoutes
 * a base page that does not have permission requirements
 * all roles can be accessed
 */
export const constantRoutes = [
  {
    path: "/redirect",
    component: Layout,
    hidden: true,
    hiddenOnRol: true,
    children: [
      {
        path: "/redirect/:path*",
        component: () => import("@/views/redirect/index"),
        hiddenOnRol: true,
      },
    ],
  },
  {
    path: "/setup",
    name: "setup",
    component: () => {
      if (window.location.pathname !== "/setup") {
        window.location.href = "/setup";
      }
      return import("@/views/setup/index");
    },
    hidden: true,
    hiddenOnRol: true,
  },
  {
    path: "/login",
    name: "login",
    component: () => {
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return import("@/views/login/index");
    },
    hidden: true,
    hiddenOnRol: true,
  },
  {
    path: "/auth-redirect",
    component: () => import("@/views/login/auth-redirect"),
    hidden: true,
    hiddenOnRol: true,
  },
  {
    path: "/404",
    component: () => import("@/views/error-page/404"),
    hidden: true,
    hiddenOnRol: true,
  },
  {
    path: "/401",
    component: () => import("@/views/error-page/401"),
    hidden: true,
    hiddenOnRol: true,
  },
  {
    path: "/403",
    component: Layout,
    hidden: true,
    hiddenOnRol: true,
    children: [
      {
        path: "/403",
        component: () => import("@/views/error-page/403"),
        hidden: true,
        hiddenOnRol: true,
      },
    ],
  },
  ...(isAreaSanitariaDeVigo() ? areaSanitariaDeVigoRoutes : []),
];

/**
 * asyncRoutes
 * the routes that need to be dynamically loaded based on user roles
 */
export async function asyncRoutes() {
  return [
    {
      path: "/",
      redirect: "/home",
      component: Layout,
      hidden: true,
      children: [
        {
          path: "home",
          component: () => import("@/views/home/index"),
          name: "home",
          meta: {
            title: "Inicio",
            nameTab: "Principal",
            meta: {
              roles: getRolesForRoute("home"),
            },
          },
        },
      ],
    },
    {
      path: "/fleet",
      component: Layout,
      hidden: true,
      children: [
        {
          path: "",
          component: () => import("@/views/fleet/index"),
          name: "fleet",
          meta: {
            nameTab: "Flota",
            roles: getRolesForRoute("fleet"),
          },
        },
      ],
    },
    {
      path: "/services",
      component: Layout,
      hidden: true,
      roles: [...getRolesForRoute("services_create"), ...getRolesForRoute("services_edit")],
      meta: {
        nameTab: "Servicios",
        persistedCache: true,
      },
      children: [
        {
          path: "edit",
          component: () => import("@/views/services"),
          name: "services_edit",
          meta: {
            nameTab: "Editar Servicio",
            roles: getRolesForRoute("services_edit"),
            hideFromTree: false,
            persistedCache: true,
          },
        },
        {
          path: "edit",
          name: "not_services_edit",
          redirect: {
            path: "/403",
            params: {
              resource: "services_edit",
            },
          },
          hidden: true,
          hiddenOnRol: true,
          meta: {
            roles: getRolesWithoutRoute("services_edit"),
            hideFromTree: true,
            persistedCache: true,
          },
        },
        {
          path: "create/:id(\\d+)",
          component: () => import("@/views/services"),
          name: "services_create",
          meta: {
            nameTab: "Nuevo Servicio",
            roles: getRolesForRoute("services_create"),
            persistedCache: true,
          },
        },
      ],
    },
    {
      path: "/calls",
      component: Layout,
      base: "/",
      meta: {
        title: "Llamadas",
      },
      children: [
        {
          path: "list",
          component: () => import("@/views/calls/index"),
          name: "calls",
          meta: {
            nameTab: "Llamadas",
            roles: getRolesForRoute("calls"),
          },
        },
        {
          path: "stats",
          component: () => import("@/views/calls/stats/index"),
          name: "calls_stats",
          meta: {
            nameTab: "Estadísticas de llamadas",
            roles: getRolesForRoute("calls_stats"),
          },
        },
      ],
    },
    {
      path: "/bookings",
      component: Layout,
      base: "/",
      children: [
        {
          path: "/bookings",
          component: () => import("@/views/bookings/index"),
          name: "bookings",
          meta: {
            nameTab: "Reservas",
            roles: getRolesForRoute("bookings"),
          },
        },
      ],
    },
    {
      path: "/recurrent",
      component: Layout,
      base: "/",
      children: [
        {
          path: "/recurrent",
          component: () => import("@/views/periodics/index"),
          name: "recurrent",
          meta: {
            nameTab: "Periódicos",
            roles: getRolesForRoute("recurrent"),
          },
        },
      ],
    },
    {
      path: "/historic",
      component: Layout,
      base: "/",
      children: [
        {
          path: "/historic",
          component: () => import("@/views/historic/index"),
          name: "historic",
          meta: {
            nameTab: "Historial",
            roles: getRolesForRoute("historic"),
          },
        },
      ],
    },
    {
      path: "/messages",
      component: Layout,
      children: [
        {
          path: "",
          component: () => import("@/views/messages/index"),
          name: "messages",
          meta: {
            nameTab: "Mensajes",
            roles: getRolesForRoute("messages"),
          },
        },
      ],
    },
    {
      path: "/print/stopszones",
      component: () => import("@/views/print/StopsZones.vue"),
      meta: {
        title: "Mapa de zonas y paradas",
        roles: [...getRolesForRoute("manager_zones_stops")],
        hideFromTree: true,
      },
    },
    {
      path: "/manager",
      component: Layout,
      redirect: "page",
      meta: {
        title: "Gestión",
        roles: [
          ...getRolesForRoute("manager_streets"),
          ...getRolesForRoute("manager_zones_stops"),
          ...getRolesForRoute("manager_delivery"),
          ...getRolesForRoute("manager_destination"),
          ...getRolesForRoute("manager_businesses"),
          ...getRolesForRoute("manager_operators"),
          ...getRolesForRoute("manager_trip_calculator"),
          ...getRolesForRoute("manager_driving_distance_calculator"),
          ...getRolesForRoute("manager_shift_calendar"),
        ],
      },
      children: [
        {
          path: "/manager/streets",
          component: () => import("@/views/manager/streets/index"),
          name: "manager_streets",
          meta: {
            title: "Calles",
            roles: getRolesForRoute("manager_streets"),
          },
        },
        {
          path: "/manager/zones-stops",
          component: () => import("@/views/manager/stopszones/index"),
          name: "manager_zones_stops",
          meta: {
            title: "Zonas y Paradas",
            roles: getRolesForRoute("manager_zones_stops"),
          },
        },
        {
          path: "/manager/delivery",
          component: () => import("@/views/manager/delivery/index"),
          name: "manager_delivery",
          meta: {
            title: "Reparto",
            roles: getRolesForRoute("manager_delivery"),
          },
        },
        {
          path: "/manager/operators",
          component: () => import("@/views/manager/users-operators/index"),
          name: "manager_operators",
          meta: {
            title: "Usuarios y operadoras",
            roles: getRolesForRoute("manager_operators"),
          },
        },
        {
          path: "/manager/delivery-lists",
          component: () => import("@/views/manager/delivery-lists/index.vue"),
          name: "manager_delivery_lists",
          meta: {
            title: "Listas de reparto",
            roles: getRolesForRoute("manager_delivery_lists"),
          },
        },
        {
          path: "/manager/trip-calculator",
          component: () => import("@/views/manager/trip-calculator/index"),
          name: "manager_trip_calculator",
          meta: {
            title: "Calculadora de precios",
            roles: getRolesForRoute("manager_trip_calculator"),
          },
        },
        {
          path: "/manager/driving-distance-calculator",
          component: () => import("@/views/manager/driving-distance-calculator/index"),
          name: "manager_driving_distance_calculator",
          meta: {
            title: "Distancia por carretera",
            roles: getRolesForRoute("manager_driving_distance_calculator"),
          },
        },
        {
          path: "/manager/shift-calendar",
          component: () => import("@/views/manager/shift-calendar/index"),
          name: "manager_shift_calendar",
          meta: {
            title: "Calendario de turnos",
            roles: getRolesForRoute("manager_shift_calendar"),
          },
        },
      ],
    },
    {
      path: "/admin",
      redirect: "home",
      component: Layout,
      alwaysShow: true, // will always show the root menu
      meta: {
        // nameTab: "Administración",
        title: "Administración",
        roles: [
          ...getRolesForRoute("admin_cabbies"),
          ...getRolesForRoute("admin_customers"),
          ...getRolesForRoute("admin_subscribers"),
          ...getRolesForRoute("admin_externals"),
          ...getRolesForRoute("admin_billing"),
        ],
      },
      children: [
        {
          path: "/admin/taxis",
          component: () => import("@/views/admin/taxis/index"),
          name: "admin_cabbies",
          meta: {
            title: "Taxis",
            roles: getRolesForRoute("admin_cabbies"),
          },
        },
        {
          path: "/admin/customers",
          component: () => import("@/views/admin/customers/index"),
          name: "admin_customers",
          meta: {
            title: "Clientes",
            roles: getRolesForRoute("admin_customers"),
          },
        },
        {
          path: "/admin/abonados",
          component: () => import("@/views/admin/subscribers/index"),
          name: "admin_subscribers",
          meta: {
            title: "Abonados",
            roles: getRolesForRoute("admin_subscribers"),
          },
        },
        {
          path: "/admin/externals",
          component: () => import("@/views/admin/externals/index"),
          name: "admin_externals",
          meta: {
            title: "Externos",
            roles: getRolesForRoute("admin_externals"),
          },
        },
        {
          path: "/admin/billing",
          component: () => import("@/views/admin/billing/index"),
          name: "admin_billing",
          meta: {
            nameTab: "Facturación",
            roles: getRolesForRoute("admin_billing"),
          },
        },
      ],
    },
    {
      path: "/reports",
      component: Layout,
      meta: {
        nameTab: "Informes",
      },
      children: [
        {
          path: "",
          component: () => import("@/views/reports/index"),
          name: "reports",
          meta: {
            nameTab: "Informes",
            roles: getRolesForRoute("reports"),
          },
        },
        {
          path: "periodic",
          component: () => import("@/views/reports/periodic"),
          name: "reports_periodic",
          meta: {
            nameTab: "Informes periódicos",
            roles: getRolesForRoute("reports"),
          },
        },
      ],
    },
    {
      path: "/config",
      component: Layout,
      meta: {
        title: "Configs",
        roles: getRolesForRoute("config"),
      },
      children: [
        {
          path: "",
          component: () => import("@/views/configs/index"),
          name: "config",
          meta: {
            nameTab: "Configs",
            roles: getRolesForRoute("config"),
          },
        },
      ],
    },
    {
      path: "/backups",
      component: Layout,
      meta: {
        title: "Backups",
        roles: getRolesForRoute("backups"),
      },
      children: [
        {
          path: "",
          component: () => import("@/views/backups/index"),
          name: "backups",
          meta: {
            nameTab: "Backups",
            meta: {
              roles: getRolesForRoute("backups"),
            },
          },
        },
      ],
    },
    {
      path: "/support",
      component: Layout,
      meta: {
        title: "Tickets",
        roles: getRolesForRoute("support"),
      },
      children: [
        {
          path: "",
          component: () => import("@/views/support/index"),
          name: "support",
          meta: {
            nameTab: "Tickets",
            roles: getRolesForRoute("support"),
          },
        },
      ],
    },
    {
      path: "/help",
      component: Layout,
      alwaysShow: true,
      meta: {
        title: "Ayuda",
        roles: [...getRolesForRoute("changelog")],
      },
      children: [
        {
          path: "/help/changelog",
          component: () => import("@/views/help/changelog"),
          name: "changelog",
          meta: {
            title: "Novedades de la versión",
            roles: getRolesForRoute("changelog"),
          },
        },
      ],
    },

    // Audit module routes
    ...auditRoutes(),

    // 404 page must be placed at the end !!!
    { path: "*", redirect: "/404", hidden: true, hiddenOnRol: true },
  ];
}

const createRouter = () =>
  new Router({
    // mode: 'history', // require service support
    scrollBehavior: () => ({ y: 0 }),
    routes: constantRoutes,
  });

const router = createRouter();

// Detail see: https://github.com/vuejs/vue-router/issues/1234#issuecomment-357941465
export function resetRouter() {
  const newRouter = createRouter();
  router.matcher = newRouter.matcher; // reset router
}

router.beforeEach(async (to, from, next) => {
  await store.restored;
  next();
});

export default router;
